import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

interface VMAssignment {
  id: number;
  tenant_id: number;
  vmid: number;
  node: string;
  vm_name: string | null;
  assigned_at: string;
  plan_start_date: string | null;
  plan_expire_date: string | null;
}

interface Tenant {
  id: number;
  company_name: string;
  is_active: boolean;
}

interface ProxmoxVM {
  vmid: number;
  name: string;
  node: string;
  status: string;
  type: string;
}

const VMAssignmentManagement: React.FC = () => {
  const [assignments, setAssignments] = useState<VMAssignment[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [vms, setVMs] = useState<ProxmoxVM[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<VMAssignment | null>(null);
  const [planStartDate, setPlanStartDate] = useState<string>('');
  const [planExpireDate, setPlanExpireDate] = useState<string>('');  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [selectedVM, setSelectedVM] = useState<ProxmoxVM | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch assignments
  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/vm-assignments/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
    } catch (err: any) {
      console.error('Error fetching assignments:', err);
      setError(err.response?.data?.detail || 'Failed to fetch assignments');
    }
  };

  // Fetch tenants
  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/auth/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (err: any) {
      console.error('Error fetching tenants:', err);
    }
  };

  // Fetch VMs from Proxmox
  const fetchVMs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/proxmox/vms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVMs(response.data);
    } catch (err: any) {
      console.error('Error fetching VMs:', err);
    }
  };

  useEffect(() => {
    fetchAssignments();
    fetchTenants();
    fetchVMs();
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTenant('');
    setSelectedVM(null);
  };

  const handleAssignVM = async () => {
    if (!selectedTenant || !selectedVM) {
      setError('Please select both a tenant and a VM');
      return;
    }

    if (loading) return; // Prevent multiple clicks

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/vm-assignments/`,
        {
          tenant_id: selectedTenant,
          vmid: selectedVM.vmid,
          node: selectedVM.node,
          vm_name: selectedVM.name
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccess('VM assigned successfully');
      handleCloseDialog();
      await fetchAssignments();
      setLoading(false);
    } catch (err: any) {
      console.error('Error assigning VM:', err);
      setError(err.response?.data?.detail || 'Failed to assign VM');
      setLoading(false); // Re-enable only on error
    }
  };

  const handleUnassignVM = async (assignment: VMAssignment) => {
    if (!window.confirm(`Unassign VM ${assignment.vm_name || assignment.vmid} from tenant?`)) {
      return;
    }

    if (loading) return; // Prevent multiple clicks
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/vm-assignments/`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          tenant_id: assignment.tenant_id,
          vmid: assignment.vmid,
          node: assignment.node
        }
      });
      setSuccess('VM unassigned successfully');
      await fetchAssignments();
      setLoading(false);
    } catch (err: any) {
      console.error('Error unassigning VM:', err);
      setError(err.response?.data?.detail || 'Failed to unassign VM');
      setLoading(false); // Re-enable only on error
    }
  };

  const handleOpenPlanDialog = (assignment: VMAssignment) => {
    setEditingAssignment(assignment);
    setPlanStartDate(assignment.plan_start_date ? assignment.plan_start_date.split('T')[0] : '');
    setPlanExpireDate(assignment.plan_expire_date ? assignment.plan_expire_date.split('T')[0] : '');
    setOpenPlanDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleClosePlanDialog = () => {
    setOpenPlanDialog(false);
    setEditingAssignment(null);
    setPlanStartDate('');
    setPlanExpireDate('');
  };

  const handleUpdatePlanDates = async () => {
    if (!editingAssignment) return;

    if (loading) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/vm-assignments/${editingAssignment.id}`,
        {
          plan_start_date: planStartDate || null,
          plan_expire_date: planExpireDate || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccess('Plan dates updated successfully');
      handleClosePlanDialog();
      await fetchAssignments();
      setLoading(false);
    } catch (err: any) {
      console.error('Error updating plan dates:', err);
      setError(err.response?.data?.detail || 'Failed to update plan dates');
      setLoading(false);
    }
  };

  // Get tenant name by ID
  const getTenantName = (tenantId: number) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.company_name || `Tenant ${tenantId}`;
  };

  // Filter available VMs (not already assigned)
  const availableVMs = vms.filter(vm => {
    return !assignments.some(a => a.vmid === vm.vmid && a.node === vm.node);
  });

  const columns: GridColDef[] = [
    {
      field: 'tenant',
      headerName: 'Tenant',
      width: 200,
      valueGetter: (params: any) => getTenantName(params.row.tenant_id)
    },
    { field: 'vm_name', headerName: 'VM Name', width: 200 },
    { field: 'vmid', headerName: 'VM ID', width: 100 },
    { field: 'node', headerName: 'Node', width: 150 },
    {
      field: 'assigned_at',
      headerName: 'Assigned At',
      width: 180,
      valueFormatter: (params: any) => {
        return new Date(params.value).toLocaleString();
      }
    },
    {
      field: 'plan_start_date',
      headerName: 'Plan Start',
      width: 130,
      renderCell: (params) => (
        params.value ? new Date(params.value).toLocaleDateString() : '-'
      )
    },
    {
      field: 'plan_expire_date',
      headerName: 'Plan Expires',
      width: 130,
      renderCell: (params) => {
        if (!params.value) return '-';
        const expireDate = new Date(params.value);
        const today = new Date();
        const isExpired = expireDate < today;
        return (
          <Chip
            label={expireDate.toLocaleDateString()}
            size="small"
            color={isExpired ? 'error' : 'default'}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenPlanDialog(params.row)}
            title="Edit Plan Dates"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => handleUnassignVM(params.row)}
            title="Unassign"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">VM Assignment Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Assign VM to Tenant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={assignments}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Assign VM Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign VM to Tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              select
              label="Select Tenant"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(Number(e.target.value))}
              fullWidth
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.company_name}
                  {!tenant.is_active && <Chip label="Inactive" size="small" sx={{ ml: 1 }} />}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Select VM"
              value={selectedVM ? `${selectedVM.node}-${selectedVM.vmid}` : ''}
              onChange={(e) => {
                const [node, vmidStr] = e.target.value.split('-');
                const vmid = Number(vmidStr);
                const vm = availableVMs.find(v => v.vmid === vmid && v.node === node);
                setSelectedVM(vm || null);
              }}
              fullWidth
              helperText={availableVMs.length === 0 ? 'All VMs are already assigned' : `${availableVMs.length} VM(s) available`}
            >
              {availableVMs.length === 0 ? (
                <MenuItem disabled value="">No available VMs</MenuItem>
              ) : (
                availableVMs.map((vm) => (
                  <MenuItem key={`${vm.node}-${vm.vmid}`} value={`${vm.node}-${vm.vmid}`}>
                    {vm.name} (ID: {vm.vmid}, Node: {vm.node})
                    <Chip
                      label={vm.status}
                      size="small"
                      color={vm.status === 'running' ? 'success' : 'default'}
                      sx={{ ml: 1 }}
                    />
                  </MenuItem>
                ))
              )}
            </TextField>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleAssignVM}
            variant="contained"
            disabled={!selectedTenant || !selectedVM || loading}
          >
            {loading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dates Dialog */}
      <Dialog open={openPlanDialog} onClose={handleClosePlanDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Plan Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {editingAssignment && (
              <Alert severity="info">
                Editing plan for VM: {editingAssignment.vm_name || editingAssignment.vmid}
              </Alert>
            )}

            <TextField
              label="Plan Start Date"
              type="date"
              value={planStartDate}
              onChange={(e) => setPlanStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to clear"
            />

            <TextField
              label="Plan Expire Date"
              type="date"
              value={planExpireDate}
              onChange={(e) => setPlanExpireDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to clear"
            />

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlanDialog}>Cancel</Button>
          <Button
            onClick={handleUpdatePlanDates}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VMAssignmentManagement;
